import { Row } from "antd";
import TableFilter from "components/tableFilter/tableFilter";
import { useFetchFeePlansQuery } from "features/feePlans/feePlansAPI";
import {
  useFetchBackendConstantsQuery,
  useFetchAgencyWorkflowStates,
} from "features/home/agencyPortal/homeAPI";
import {
  useFetchCreditorSummariesQuery,
  useFetchCreditorTagsQuery,
} from "features/creditors/agencyPortal/creditorsAPI";
import { useFetchDebtorTagsQuery } from "features/tags/tagsAPI";
import PaymentAllocationTable from "pages/agencyPortal/payments/paymentAllocationTable";
import { useState } from "react";

const initialFilterState = {
  scheduledDate: null,
  createdAt: null,
  status: {
    include: [],
    exclude: [],
  },
  isSettlement: null,
  paymentMethod: {
    include: [],
    exclude: [],
  },
  allocationMethod: {
    include: [],
    exclude: [],
  },
  paymentAllocationAmount: {
    min: null,
    max: null,
  },
  creditorFeePlan: {
    include: [],
    exclude: [],
  },
  forwardingEntityFeePlan: {
    include: [],
    exclude: [],
  },
  debtorTags: [],
  creditorTags: [],
  debtTypes: [],
  creditor: [],
  originalCreditorNames: [],
  debtorStates: [],
  accountStatuses: {
    include: [],
    exclude: [],
  },
};

const ALLOCATION_METHOD_OPTIONS = [
  { label: "Automatic", value: "creditor_payment_category_priority" },
  { label: "Manual", value: "preallocation_and_reallocation" },
];

export default function PaymentAllocationsTab() {
  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: feePlans } = useFetchFeePlansQuery();
  const { data: debtorTags } = useFetchDebtorTagsQuery();
  const { data: creditorTags } = useFetchCreditorTagsQuery();
  const { data: creditors } = useFetchCreditorSummariesQuery();
  const { data: accountWorkflowStates } = useFetchAgencyWorkflowStates();
  const [filterStates, setFilterStates] = useState(initialFilterState);

  function setFilterProperty(name, value) {
    setFilterStates((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  }

  // This is the structure of the filters that will be displayed on the page
  const filteredStructure = [
    {
      title: "Scheduled Date",
      name: "scheduledDate",
      type: "date",
      onChange: (event) => setFilterProperty("scheduledDate", event),
    },
    {
      title: "Created At",
      name: "createdAt",
      type: "date",
      onChange: (event) => setFilterProperty("createdAt", event),
    },
    {
      title: "Payment Status",
      name: "status",
      type: "include/exclude",
      include: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("status", { ...filterStates.status, include: event });
        },
        options: constants?.paymentStatus?.map((d) => ({
          label: d.display,
          value: d.value,
        })),
        placeholder: "Select Status...",
      },
      exclude: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("status", { ...filterStates.status, exclude: event });
        },
        options: constants?.paymentStatus?.map((d) => ({
          label: d.display,
          value: d.value,
        })),
        placeholder: "Select Status...",
      },
    },
    {
      title: "Settlement",
      name: "isSettlement",
      type: "checkbox-option",
      options: [
        { label: "Settled", value: "settled" },
        { label: "Not Settled", value: "not_settled" },
      ],
      onChange: (event) =>
        setFilterProperty(
          "isSettlement",
          event.target.value && event.target.checked ? event.target.value : null,
        ),
      value: filterStates.isSettlement,
    },
    {
      title: "Payment Method",
      name: "paymentMethod",
      type: "include/exclude",
      include: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("paymentMethod", { ...filterStates.paymentMethod, include: event });
        },
        options: constants?.paymentMethodTypes?.map((d) => ({
          label: d.display,
          value: d.value,
        })),
        placeholder: "Select Payment Methods...",
      },
      exclude: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("paymentMethod", { ...filterStates.paymentMethod, exclude: event });
        },
        options: constants?.paymentMethodTypes?.map((d) => ({
          label: d.display,
          value: d.value,
        })),
        placeholder: "Select Payment Methods...",
      },
    },
    {
      title: "Allocation Method",
      name: "allocationMethod",
      type: "include/exclude",
      include: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("allocationMethod", {
            ...filterStates.allocationMethod,
            include: event,
          });
        },
        options: ALLOCATION_METHOD_OPTIONS,
        placeholder: "Select Allocation Methods...",
      },
      exclude: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("allocationMethod", {
            ...filterStates.allocationMethod,
            exclude: event,
          });
        },
        options: ALLOCATION_METHOD_OPTIONS,
        placeholder: "Select Allocation Methods...",
      },
    },
    {
      title: "Payment Allocation Amount",
      name: "paymentAllocation",
      type: "min/max",
      min: {
        prefix: "$",
        step: "0.01",
        min: "0",
        onChange: (event) =>
          setFilterProperty("paymentAllocationAmount", {
            ...filterStates.paymentAllocationAmount,
            min: event,
          }),
      },
      max: {
        prefix: "$",
        step: "0.01",
        min: "0",
        onChange: (event) =>
          setFilterProperty("paymentAllocationAmount", {
            ...filterStates.paymentAllocationAmount,
            max: event,
          }),
      },
    },
    {
      title: "Creditor Fee Plan",
      name: "creditorFeePlan",
      type: "include/exclude",
      include: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("creditorFeePlan", { ...filterStates.creditorFeePlan, include: event });
        },
        options: feePlans?.map((d) => ({
          label: d.name,
          value: d.id,
        })),
        placeholder: "Select Fee Plan...",
      },
      exclude: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("creditorFeePlan", { ...filterStates.creditorFeePlan, exclude: event });
        },
        options: feePlans?.map((d) => ({
          label: d.name,
          value: d.id,
        })),
        placeholder: "Select Fee Plan...",
      },
    },
    {
      title: "Forwarding Entity Fee Plan",
      name: "forwardingEntityFeePlan",
      type: "include/exclude",
      include: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("forwardingEntityFeePlan", {
            ...filterStates.forwardingEntityFeePlan,
            include: event,
          });
        },
        options: feePlans?.map((d) => ({
          label: d.name,
          value: d.id,
        })),
        placeholder: "Select Fee Plan...",
      },
      exclude: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("forwardingEntityFeePlan", {
            ...filterStates.forwardingEntityFeePlan,
            exclude: event,
          });
        },
        options: feePlans?.map((d) => ({
          label: d.name,
          value: d.id,
        })),
        placeholder: "Select Fee Plan...",
      },
    },
    {
      title: "Debtor Tags",
      name: "debtorTags",
      type: "dropdown",
      dropdown: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("debtorTags", event);
        },
        options: debtorTags?.map((tag) => ({
          value: tag.id,
          label: tag.name,
        })),
        placeholder: "Select Debtor Tags...",
      },
    },
    {
      title: "Client Tags",
      name: "creditorTags",
      type: "dropdown",
      dropdown: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("creditorTags", event);
        },
        options: creditorTags?.map((tag) => ({
          value: tag.id,
          label: tag.name,
        })),
        placeholder: "Select Client Tags...",
      },
    },
    {
      title: "Debt Type",
      name: "debtTypes",
      type: "checkbox-group",
      options: [
        { label: "Consumer", value: "consumer" },
        { label: "Commercial", value: "commercial" },
      ],
      onChange: (event) => setFilterProperty("debtTypes", event),
      value: filterStates.debtTypes,
    },
    {
      title: "Client",
      name: "creditor",
      type: "dropdown",
      dropdown: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("creditor", event);
        },
        options: creditors?.map((creditor) => ({
          value: creditor.id,
          label: creditor.code ? `${creditor.name} (${creditor.code})` : creditor.name,
        })),
        placeholder: "Select Clients...",
      },
    },
    {
      title: "Client Tags",
      name: "creditorTags",
      type: "dropdown",
      dropdown: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("creditorTags", event);
        },
        options: creditorTags?.map((tag) => ({
          value: tag.id,
          label: tag.name,
        })),
        placeholder: "Select Client Tags...",
      },
    },
    {
      title: "Original Creditor",
      name: "originalCreditorNames",
      type: "tags",
      placeholder: "Name (case insensitive)",
      value: filterStates.originalCreditorNames,
      onChange: (event) => {
        setFilterProperty("originalCreditorNames", event);
      },
    },
    {
      title: "Account Status",
      name: "accountStatuses",
      type: "include/exclude",
      include: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("accountStatuses", { ...filterStates.accountStatuses, include: event });
        },
        options: accountWorkflowStates?.map((d) => ({
          label: d.name,
          value: d.code,
        })),
        placeholder: "Select Account Status...",
      },
      exclude: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("accountStatuses", { ...filterStates.accountStatuses, exclude: event });
        },
        options: accountWorkflowStates?.map((d) => ({
          label: d.name,
          value: d.code,
        })),
        placeholder: "Select Account Status...",
      },
    },
    {
      title: "Debtor Location",
      name: "debtorStates",
      type: "dropdown",
      dropdown: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("debtorStates", event);
        },
        options: [
          {
            label: "US States",
            options:
              constants?.states?.map((state) => ({
                value: state.value,
                label: `${state.display} (${state.value})`,
              })) ?? [],
          },
          {
            label: "Canadian Provinces",
            options:
              constants?.canadianProvinces?.map((province) => ({
                value: province.value,
                label: `${province.display} (${province.value})`,
              })) ?? [],
          },
        ],
        placeholder: "Select States...",
      },
    },
  ];

  /**
   * Convert the filter states to query params
   * @returns {Object} queryParams - The query parameters
   * @returns {string} queryParams.scheduledDateStart - The start date for scheduling
   * @returns {string} queryParams.scheduledDateEnd - The end date for scheduling
   * @returns {string} queryParams.createdAtStart - The start date for creation
   * @returns {string} queryParams.createdAtEnd - The end date for creation
   * @returns {string[]} queryParams.includeStatusCodes - The status codes to include
   * @returns {string[]} queryParams.excludeStatusCodes - The status codes to exclude
   * @returns {boolean} queryParams.isSettlement - Whether it is a settlement
   * @returns {string[]} queryParams.includePaymentMethodTypes - The payment methods to include
   * @returns {string[]} queryParams.excludePaymentMethodTypes - The payment methods to exclude
   * @returns {string} queryParams.minTotalAmount - The minimum total amount
   * @returns {string} queryParams.maxTotalAmount - The maximum total amount
   * @returns {string[]} queryParams.includeCreditorFeePlanIds - The creditor fee plan IDs to include
   * @returns {string[]} queryParams.excludeCreditorFeePlanIds - The creditor fee plan IDs to exclude
   * @returns {string[]} queryParams.includeForwardingEntityFeePlanIds - The forwarding entity fee plan IDs to include
   * @returns {string[]} queryParams.excludeForwardingEntityFeePlanIds - The forwarding entity fee plan IDs to exclude
   */
  function convertStateToQueryParams() {
    const queryParams = {};
    if (filterStates.scheduledDate) {
      const [scheduledDateStart, scheduledDateEnd] = filterStates.scheduledDate;
      if (scheduledDateStart) {
        queryParams.scheduledDateStart = scheduledDateStart;
      }
      if (scheduledDateEnd) {
        queryParams.scheduledDateEnd = scheduledDateEnd;
      }
    }

    if (filterStates.createdAt) {
      const [createdAtStart, createdAtEnd] = filterStates.createdAt;
      if (createdAtStart) {
        queryParams.createdAtStart = createdAtStart;
      }
      if (createdAtEnd) {
        queryParams.createdAtEnd = createdAtEnd;
      }
    }

    if (filterStates.status.include.length > 0) {
      queryParams.includeStatusCodes = filterStates.status.include;
    }
    if (filterStates.status.exclude.length > 0) {
      queryParams.excludeStatusCodes = filterStates.status.exclude;
    }
    if (filterStates.isSettlement) {
      queryParams.isSettlement = filterStates.isSettlement === "settled";
    }

    if (filterStates.paymentMethod.include.length > 0) {
      queryParams.includePaymentMethodTypes = filterStates.paymentMethod.include;
    }
    if (filterStates.paymentMethod.exclude.length > 0) {
      queryParams.excludePaymentMethodTypes = filterStates.paymentMethod.exclude;
    }

    if (filterStates.allocationMethod.include.length > 0) {
      queryParams.includeAllocationMethods = filterStates.allocationMethod.include;
    }
    if (filterStates.allocationMethod.exclude.length > 0) {
      queryParams.excludeAllocationMethods = filterStates.allocationMethod.exclude;
    }

    if (filterStates.paymentAllocationAmount.min) {
      queryParams.minTotalAmount = filterStates.paymentAllocationAmount.min;
    }

    if (filterStates.paymentAllocationAmount.max) {
      queryParams.maxTotalAmount = filterStates.paymentAllocationAmount.max;
    }

    if (filterStates.creditorFeePlan.include.length > 0) {
      queryParams.includeCreditorFeePlanIds = filterStates.creditorFeePlan.include;
    }
    if (filterStates.creditorFeePlan.exclude.length > 0) {
      queryParams.excludeCreditorFeePlanIds = filterStates.creditorFeePlan.exclude;
    }

    if (filterStates.forwardingEntityFeePlan.include.length > 0) {
      queryParams.includeForwardingEntityFeePlanIds = filterStates.forwardingEntityFeePlan.include;
    }
    if (filterStates.forwardingEntityFeePlan.exclude.length > 0) {
      queryParams.excludeForwardingEntityFeePlanIds = filterStates.forwardingEntityFeePlan.exclude;
    }

    if (filterStates.debtorTags.length > 0) {
      queryParams.includeDebtorTagIds = filterStates.debtorTags;
    }

    if (filterStates.creditorTags.length > 0) {
      queryParams.includeCreditorTagIds = filterStates.creditorTags;
    }

    if (filterStates.debtTypes.length > 0) {
      queryParams.includeDebtTypes = filterStates.debtTypes;
    }

    if (filterStates.creditor.length > 0) {
      queryParams.includeCreditorIds = filterStates.creditor;
    }

    if (filterStates.originalCreditorNames.length > 0) {
      queryParams.includeOriginalCreditorNames = filterStates.originalCreditorNames;
    }

    if (filterStates.debtorStates.length > 0) {
      queryParams.includeDebtorStates = filterStates.debtorStates;
    }

    if (filterStates.accountStatuses.include.length > 0) {
      queryParams.includeAccountStatusCodes = filterStates.accountStatuses.include;
    }
    if (filterStates.accountStatuses.exclude.length > 0) {
      queryParams.excludeAccountStatusCodes = filterStates.accountStatuses.exclude;
    }

    return queryParams;
  }

  function resetFilters() {
    setFilterStates(initialFilterState);
  }

  return (
    <Row wrap={false}>
      <TableFilter filteredStructure={filteredStructure} onReset={() => resetFilters()} />
      <PaymentAllocationTable filterParams={convertStateToQueryParams()} />
    </Row>
  );
}
